import React, { useState } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { formatPrice } from 'common/formatters';
import { getDefaultInternetAmenityPlan } from 'common/internetAmenityPlans';
import { canAccessActivation } from 'common/utility/helpers';
import {
  UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED,
  UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_PARTNER_CUSTOMER_TYPE_TENANT_SCREENING,
  UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID,
} from 'common/utility/constants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrCircularProgressBar from 'client/components/SrCircularProgressBar/SrCircularProgressBar';
import SrUtilitySetupEditLeaseStartDateModal from 'client/components/SrUtilitySetup/SrUtilitySetupEditLeaseStartDateModal';
import SrUtilityProfitOptionCard from 'client/components/SrUtilityProfitOptionCard/SrUtilityProfitOptionCard';
import SrUtilitySetupNotRequiredTypes from 'client/components/SrUtilitySetup/SrUtilitySetupNotRequiredTypes';
import UPResidualUtilityCard from 'client/components/UPResidualUtilityCard/UPResidualUtilityCard';
import SrUtilitySetupLazySignup from 'client/components/SrUtilitySetup/SrUtilitySetupLazySignup';

import './SrUtilitySetupOverview.less';

const SrUtilitySetupOverview = ({
  utilityTypes,
  utilityPartner,
  utilityActivation,
  utilityUser,
  activationInternetProviders,
}) => {
  const [isEditLeaseStartDateModalOpen, setIsEditLeaseStartDateModalOpen] = useState(
    !utilityActivation.leaseStartDate && !!utilityActivation.user,
  );
  const [showLoginModal, setShowLoginModal] = useState(
    !canAccessActivation({
      utilityActivation,
      utilityUser,
    }),
  );

  const completedItems = utilityActivation.items.filter(
    (item) =>
      item.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED ||
      item.status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED ||
      item.status === UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  );
  const completionProgress =
    Math.round((completedItems.length * 100) / utilityActivation.items.length) || 5;
  const isTenantScreeningPartner =
    utilityPartner.customerType === UTILITY_PARTNER_CUSTOMER_TYPE_TENANT_SCREENING;

  const getLink = (item) => {
    if (item.status === UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER) {
      return null;
    }

    const hasPlans = item.availableProviders.some((provider) => provider.plans.length > 0);
    const accountRequested = item.status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED;

    return lodashCompact([
      '/utility-setup',
      utilityPartner.slug,
      utilityActivation.shortId,
      item.type.label.toLowerCase(),
      hasPlans ? 'plan' : 'provider',
      accountRequested && item.utilityProviderId,
      accountRequested && (item.utilityProviderPlanId || item.utilityInternetPlanId),
      accountRequested && item.plan?.utilityProviderPlanVariantId,
    ]).join('/');
  };

  const getSubtitle = (item) => {
    if (item.status === UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER) {
      return 'Analyzing options... come back in 10min.';
    }

    if (item.type.id !== UTILITY_TYPE_ID_INTERNET) {
      return item.availableProviders.length > 1
        ? 'See options for your address.'
        : 'See the only option for your address.';
    }

    const availableProvidersSubset = (item?.availableProviders || []).slice(0, 2);
    const availableProvidersNames = availableProvidersSubset.map((provider) => provider.title);
    const startingAtPrice = formatPrice(
      Math.min(
        ...availableProvidersSubset.map((provider) => {
          return Math.min(...provider.plans.map((plan) => plan.price));
        }),
      ),
    );
    return (
      <div>
        <SrText weight="800" fontSize="12px">
          {availableProvidersNames.join(' and ')}
        </SrText>
        &nbsp;starting at {startingAtPrice}/month.
      </div>
    );
  };

  const defaultInternetAmenityPlan = getDefaultInternetAmenityPlan(activationInternetProviders);
  const defaultInternetAmenityPlanProvider = activationInternetProviders.find(({ plans = [] }) =>
    plans.find((plan) => {
      return plan.internetAmenityPlanId === defaultInternetAmenityPlan?.internetAmenityPlanId;
    }),
  );

  return (
    <div styleName="view">
      <div styleName="titleBox">
        {completionProgress !== 100 && (
          <>
            <span styleName="goldText">
              {isTenantScreeningPartner ? 'Turn on your utilities' : 'Get your key'}
            </span>
            <br />
            <span>
              {isTenantScreeningPartner ? 'before you move-in.' : 'by turning on your utilities.'}
            </span>
          </>
        )}
        {completionProgress === 100 && (
          <span styleName="greenText">
            {isTenantScreeningPartner ? (
              'Wohoo! Your utilities are completed.'
            ) : (
              <>
                Congrats! You will get
                <br /> move-in details from us soon.
              </>
            )}
          </span>
        )}
      </div>

      <SrBox margin="20px 0 21px">
        <SrCircularProgressBar
          width={128}
          strokeWidth={8}
          strokeColor="#00A238"
          iconUrl={isTenantScreeningPartner ? '/images/emoji-house.png' : '/images/emoji-key.png'}
          progress={completionProgress}
        />
        <div styleName="progressPercentText">
          {completionProgress}% complete {completionProgress === 100 && '🎉'}
        </div>
      </SrBox>

      <SrBox styleName="itemsBox">
        {utilityActivation.items.map((item) => {
          const link = getLink(item);
          const subtitle = getSubtitle(item);
          const accountRequested = item.status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED;
          const analyzing = item.status === UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER;

          if (
            item.type.id === UTILITY_TYPE_ID_INTERNET &&
            defaultInternetAmenityPlan &&
            defaultInternetAmenityPlanProvider?.purchaseFlowType !==
              UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID &&
            utilityActivation.hasInternetAmenityEligibility
          ) {
            return (
              <UPResidualUtilityCard
                key={item.id}
                utilityPlan={item.plan}
                utilityProvider={defaultInternetAmenityPlanProvider}
                utilityType={item.type}
                utilityPartner={utilityPartner}
                utilityActivation={utilityActivation}
                defaultInternetAmenityPlan={defaultInternetAmenityPlan}
                completed={
                  item.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED ||
                  item.status === UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT ||
                  accountRequested
                }
              />
            );
          }

          return (
            <SrUtilityProfitOptionCard
              key={item.id}
              link={link}
              title={item.type.label}
              subtitle={subtitle}
              detailIconUrl={item.type.iconUrl}
              detailColor={item.type.iconBackgroundColor}
              imageUrl={item.type.imageUrl}
              processing={accountRequested}
              analyzing={analyzing}
              completed={
                item.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED ||
                item.status === UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT ||
                accountRequested
              }
              showArrow={!analyzing}
            />
          );
        })}
        <SrUtilitySetupNotRequiredTypes
          utilityTypes={utilityTypes || []}
          utilityActivation={utilityActivation}
        />
      </SrBox>

      {isEditLeaseStartDateModalOpen && (
        <SrUtilitySetupEditLeaseStartDateModal
          utilityActivation={utilityActivation}
          onClose={() => setIsEditLeaseStartDateModalOpen(false)}
        />
      )}

      {showLoginModal && (
        <SrUtilitySetupLazySignup
          utilityActivation={utilityActivation}
          callbackFunction={() => {
            setShowLoginModal(false);
          }}
        />
      )}
    </div>
  );
};

SrUtilitySetupOverview.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  utilityActivation: PropTypes.object.isRequired,
};

SrUtilitySetupOverview.displayName = 'SrUtilitySetupOverview';

export default SrUtilitySetupOverview;
